import type * as api from '@gitbook/api-client';

import { apiResource, usePaginatedAPIQuery } from '../../state';

/**
 * Queries a paginated list of open or resolved comments for the given Space or Change Request.
 */
export function useListPaginatedComments(props: {
    spaceId: string;
    changeRequestId?: string;

    /**
     * The page ID to fetch comments for.
     */
    pageId?: string;

    /**
     * The status to filter for.
     */
    status?: 'all' | 'open' | 'resolved';

    /**
     * The authors to filter for.
     */
    authors?: Set<string>;

    /**
     * Whether to skip the query.
     */
    skip?: boolean;

    /**
     * Whether to suspense when fetching the initial page.
     */
    suspense?: boolean;
}): [api.Comment[], (() => Promise<void>) | undefined, (() => Promise<void>) | undefined] {
    const {
        spaceId,
        changeRequestId,
        pageId,
        status = 'open',
        authors = new Set(),
        skip = false,
        suspense = true,
    } = props;

    const queryParams = {
        order: 'desc',
        limit: 100,
        status,
        targetPage: pageId,
        ...(authors.size > 0 ? { authors: Array.from(authors) } : {}),
    } as const;

    const { data, next, previous } = usePaginatedAPIQuery(
        changeRequestId
            ? apiResource('listCommentsInChangeRequest', {
                  spaceId,
                  changeRequestId,
                  ...queryParams,
              })
            : apiResource('listCommentsInSpace', { spaceId, ...queryParams }),
        { skip, suspense }
    );

    return [data?.items ?? [], next, previous];
}
